/**
 * @ngdoc service
 * @name runnerStartFlowsFormatter
 * @module flowingly.runner.services
 *
 * @description Service repsonsible for formatting flows available (can start)
 *
 *
 * ###API
 * * groupFlowModelsByCategory - format the server response into something the client side can consume.
 */
import ICategoryTree from '@Shared.Angular/@types/core/contracts/interfaces/categoryTree';
import { Guid } from '@Shared.Angular/@types/guid';
import angular from 'angular';

export interface IFormattedCategory<T> {
  name: string;
  id: Guid | number;
  flowModels: T[];
  subCategories: IFormattedCategory<T>[];
}

export class RunnerStartFlowsFormatter {
  public static ID = 'runnerStartFlowsFormatter';
  static $inject = [];

  public groupFlowModelsByCategory<T extends ICategoryTree>(
    flowModels: T[],
    sortFlowModelsInsideCategoryFunc = null
  ) {
    if (flowModels.length === 0) {
      return [];
    }
    const categories = this.doGroupingForNestedCategories<T>(flowModels);

    if (sortFlowModelsInsideCategoryFunc) {
      categories.forEach(sortFlowModelsInsideCategoryFunc);
    }

    return categories;
  }

  private doGroupingForNestedCategories<T extends ICategoryTree>(
    flowModels: T[]
  ) {
    const topLevelCategories = new Map<Guid | number, IFormattedCategory<T>>();
    const noCategoryId = 0;
    flowModels.forEach((flowModel) => {
      let categoryFromTree = flowModel.categoryTree;
      if (categoryFromTree === null) {
        if (!topLevelCategories.has(noCategoryId)) {
          const category = {
            name: 'No Category',
            id: noCategoryId,
            flowModels: [],
            subCategories: []
          };
          topLevelCategories.set(noCategoryId, category);
        }
        topLevelCategories.get(noCategoryId).flowModels.push(flowModel);
        return;
      }
      if (!topLevelCategories.has(categoryFromTree.categoryId)) {
        const category = {
          name: categoryFromTree.categoryName,
          id: categoryFromTree.categoryId,
          flowModels: [],
          subCategories: []
        };
        topLevelCategories.set(categoryFromTree.categoryId, category);
      }
      let parentCategory = topLevelCategories.get(categoryFromTree.categoryId);
      categoryFromTree = categoryFromTree.category;
      while (categoryFromTree) {
        const existingCategory = parentCategory.subCategories.find(
          (c) => c.id === categoryFromTree.categoryId
        );
        if (existingCategory) {
          parentCategory = existingCategory;
        } else {
          const category = {
            name: categoryFromTree.categoryName,
            id: categoryFromTree.categoryId,
            flowModels: [],
            subCategories: []
          };
          parentCategory.subCategories.push(category);
          parentCategory.subCategories = orderItemsAlphabetically(
            parentCategory.subCategories
          );
          parentCategory = category;
        }
        categoryFromTree = categoryFromTree.category;
      }
      parentCategory.flowModels.push(flowModel);
    });
    return orderItemsAlphabetically(Array.from(topLevelCategories.values()));
  }
}
function orderItemsAlphabetically<T>(categories: IFormattedCategory<T>[]) {
  return categories.sort(function (a, b) {
    return a.name.localeCompare(b.name, undefined, {
      numeric: true,
      sensitivity: 'base'
    });
  });
}

angular
  .module('flowingly.runner.services')
  .service(RunnerStartFlowsFormatter.ID, RunnerStartFlowsFormatter);

export type RunnerStartFlowsFormatterType = InstanceType<
  typeof RunnerStartFlowsFormatter
>;
